import {IOfferBoxOffer, OfferType as OfferBoxType} from "../mocks/singleOfferMock";
import {ICbiMainImage, ICbiMapOfferDetail} from "./cbi_map.types";

const quarterStartMonth: Record<string, number> = {
    Q1: 1,
    Q2: 4,
    Q3: 7,
    Q4: 10
};

const parseQuarterToDate = (value: string | null | undefined, endOfQuarter = false): string => {
    if (!value) {
        return "1970-01-01";
    }
    const [quarter, year] = value.trim().split(" ");
    const startMonth = quarterStartMonth[quarter];
    const parsedYear = Number(year);
    if (!startMonth || !Number.isFinite(parsedYear)) {
        return "1970-01-01";
    }
    if (!endOfQuarter) {
        return `${parsedYear}-${startMonth.toString().padStart(2, "0")}-01`;
    }
    const endMonth = startMonth + 2;
    const endDate = new Date(Date.UTC(parsedYear, endMonth, 0));
    return `${parsedYear}-${endMonth.toString().padStart(2, "0")}-${endDate.getUTCDate().toString().padStart(2, "0")}`;
};

const formatAddress = (detail: ICbiMapOfferDetail): string => {
    const address = detail.address;
    if (!address) {
        return detail.name;
    }
    const street = [address.street_name, address.street_number].filter(Boolean).join(" ");
    const cityPart = [address.city, address.city_district].filter(Boolean).join(", ");
    const postal = address.postal_code ? `${address.postal_code} ` : "";
    return [street, `${postal}${cityPart}`].filter(Boolean).join(", ");
};

const mapStatsToOfferBoxStats = (detail: ICbiMapOfferDetail): IOfferBoxOffer["stats"] => {
    const stats = detail.stats;
    if (stats) {
        return {
            properties_count_for_sale: stats.properties_count_for_sale,
            properties_count_total: stats.properties_count_total,
            properties_with_roi_count: stats.properties_with_roi_count,
            ranges_area_min: stats.ranges_area_min,
            ranges_area_max: stats.ranges_area_max,
            ranges_price_m2_min: stats.ranges_price_m2_min,
            ranges_price_m2_max: stats.ranges_price_m2_max,
            ranges_rooms_min: stats.ranges_rooms_min,
            ranges_rooms_max: stats.ranges_rooms_max,
            properties_avg_price_m2_all: stats.properties_avg_price_m2_all,
            rooms_structure: stats.rooms_structure ?? null
        };
    }

    return {
        properties_count_for_sale: detail.properties_count_for_sale ?? detail.properties_count_total ?? 0,
        properties_count_total: detail.properties_count_total ?? 0,
        properties_with_roi_count: 0,
        ranges_area_min: 0,
        ranges_area_max: 0,
        ranges_price_m2_min: 0,
        ranges_price_m2_max: 0,
        ranges_rooms_min: 0,
        ranges_rooms_max: 0,
        properties_avg_price_m2_all: 0
    };
};

const mapMainImageToOfferBox = (mainImage: ICbiMainImage | string | null | undefined): IOfferBoxOffer["main_image"] => {
    const headerUrl = typeof mainImage === "string" ? mainImage : (mainImage?.m_img_375x211 ?? "");
    return {
        m_img_375x211: headerUrl,
        width: 375,
        height: 211
    };
};

export const mapDetailToOfferBoxOffer = (detail: ICbiMapOfferDetail): IOfferBoxOffer => {
    return {
        id: 0,
        investmentId: detail.id,
        name: detail.name,
        rp_public_url: detail.rp_public_url ?? "",
        slug: detail.id.toLowerCase(),
        description: null,
        address: formatAddress(detail),
        geo_point: {
            coordinates: [0, 0]
        },
        main_image: mapMainImageToOfferBox(detail.main_image),
        construction_date_range: {
            lower: parseQuarterToDate(detail.construction?.start_quarter, false),
            upper: parseQuarterToDate(detail.construction?.end_quarter, true),
            bounds: "[]"
        },
        region: {
            full_name: detail.address?.city ?? "",
            id: 0,
            short_name_reverted: detail.address?.city_district || detail.address?.city || ""
        },
        street_name: detail.address?.street_name ?? null,
        street_number: detail.address?.street_number ?? null,
        type: (detail.type as OfferBoxType) ?? OfferBoxType.FLAT,
        vendor: {
            id: 0,
            name: "",
            slug: ""
        },
        has_required_prices: null,
        price_type: null,
        stats: mapStatsToOfferBoxStats(detail)
    };
};
